/**
 * AI Agent Modal
 *
 * Purpose: Dialog wrapping AIAgentForm. Owns form state, validation, save/
 *          delete handlers, and the inline duplicate-name 409 surface.
 * Architecture: Self-contained — fetches skill / tool-call options on open.
 * Author: Development Team
 * Created: 2026-05-23
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
} from '@mui/material';
import {
  Business as TenantIcon,
  Delete as DeleteIcon,
  Public as GlobalIcon,
} from '@mui/icons-material';

import aiAgentService, {
  AIAgent,
  AIAgentConflict,
  AIAgentCreatePayload,
  AIAgentUpdatePayload,
  AiSkillOption,
  DEFAULT_AGENT_SETTINGS,
} from '../../../services/aiAgentService';
import { TenantInfo } from '../../../services/metadataService';
import { generateAIAgentId } from '../../../utils/patterns';
import { ConfirmDialog } from '../../common';
import AIAgentForm, { AIAgentFormData } from '../AIAgentForm';
import { SecondaryButton, DestructiveButton } from '../../common/ActionButtons';
import DialogFooter from '../../common/DialogFooter';

const _DEFAULT_OPTION = 'unspecified';

export interface AIAgentModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  aiAgent?: AIAgent;
  tenantContext: TenantInfo;
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
  onDelete?: (aiAgent: AIAgent) => void;
}

const emptyFormData = (tenantId: string): AIAgentFormData => ({
  ai_agent_id: '',
  tenant_id: tenantId,
  label: '',
  skills: [],
  tool_calls: [],
  system_prompt: '',
  settings: { ...DEFAULT_AGENT_SETTINGS },
  status: 'active',
  access: '2',
});

/** Extract a human-readable message from a core-api validation error (ORCH-286 §8).
 * `ValueError`s surface as `{detail: string}`; FastAPI/pydantic 422s surface as
 * `{detail: [{loc, msg, type}, ...]}`. */
function extractSaveErrorMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((d) =>
        d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : null
      )
      .filter((m): m is string => !!m);
    if (messages.length) return messages.join('; ');
  }
  return error instanceof Error ? error.message : fallback;
}

export const AIAgentModal: React.FC<AIAgentModalProps> = ({
  open,
  mode,
  aiAgent,
  tenantContext,
  canEdit,
  onClose,
  onSaved,
  onDelete,
}) => {
  const [formData, setFormData] = useState<AIAgentFormData>(() =>
    emptyFormData(tenantContext.tenant_id)
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<AIAgentConflict | null>(null);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [skillOptions, setSkillOptions] = useState<AiSkillOption[]>([]);
  const [toolCallOptions, setToolCallOptions] = useState<string[]>([_DEFAULT_OPTION]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const showAccessLevel = useMemo(() => {
    if (!aiAgent) {
      return tenantContext.is_global;
    }
    return aiAgent.tenant_id === 'GLOBAL';
  }, [aiAgent, tenantContext.is_global]);

  // Reset state when the modal opens.
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSaveError(null);
    setDuplicate(null);
    setRestoreConfirmOpen(false);

    if (mode === 'edit' && aiAgent) {
      setFormData({
        ai_agent_id: aiAgent.ai_agent_id,
        tenant_id: aiAgent.tenant_id,
        label: aiAgent.label,
        skills: aiAgent.skills || [],
        tool_calls: aiAgent.tool_calls || [],
        system_prompt: aiAgent.system_prompt || '',
        settings: aiAgent.settings || { ...DEFAULT_AGENT_SETTINGS },
        status: aiAgent.status === 'disabled' ? 'disabled' : 'active',
        access: aiAgent.access,
      });
    } else {
      setFormData(emptyFormData(tenantContext.tenant_id));
    }
  }, [open, mode, aiAgent, tenantContext.tenant_id]);

  // Auto-generate slug from label (create mode only).
  useEffect(() => {
    if (mode !== 'create') return;
    if (formData.label.trim()) {
      const id = generateAIAgentId(formData.label);
      setFormData((prev) => (prev.ai_agent_id === id ? prev : { ...prev, ai_agent_id: id }));
    } else if (formData.ai_agent_id) {
      setFormData((prev) => ({ ...prev, ai_agent_id: '' }));
    }
  }, [formData.label, mode, formData.ai_agent_id]);

  // Load skill / tool-call options once the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingOptions(true);
      try {
        const options = await aiAgentService.getOptions();
        if (cancelled) return;
        setSkillOptions(options.skills || []);
        setToolCallOptions(options.tool_calls.length ? options.tool_calls : [_DEFAULT_OPTION]);
      } catch (err) {
        console.error('Failed to load AI Agent options', err);
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};

    if (!formData.label.trim()) {
      next.label = 'Name is required';
    } else if (formData.label.trim().length < 2) {
      next.label = 'Name must be at least 2 characters';
    } else if (formData.label.length > 100) {
      next.label = 'Name must be under 100 characters';
    }

    if (mode === 'create') {
      if (!formData.ai_agent_id.trim()) {
        next.ai_agent_id = 'AI Agent ID is required';
      } else if (!/^aa_[a-z][a-z0-9_-]*$/.test(formData.ai_agent_id)) {
        next.ai_agent_id =
          'AI Agent ID must start with aa_ and contain only lowercase letters, numbers, hyphens, and underscores';
      }
    }

    const { max_tool_calls, phase_label, section_heading } = formData.settings;
    if (max_tool_calls < 0 || max_tool_calls > 100) {
      next['settings.max_tool_calls'] = 'Max Tool Calls must be between 0 and 100';
    }
    if (phase_label && !/^[a-z][a-z0-9_]*$/.test(phase_label)) {
      next['settings.phase_label'] =
        'Phase Label must start with a lowercase letter and contain only lowercase letters, digits, or underscores';
    }
    if (section_heading && section_heading.length > 100) {
      next['settings.section_heading'] = 'Output Section Heading must be under 100 characters';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    setSaveError(null);
    setDuplicate(null);

    if (!validate()) return;

    setLoading(true);
    try {
      if (mode === 'create') {
        const payload: AIAgentCreatePayload = {
          ai_agent_id: formData.ai_agent_id,
          tenant_id: formData.tenant_id,
          label: formData.label.trim(),
          skills: formData.skills,
          tool_calls: formData.tool_calls,
          system_prompt: formData.system_prompt,
          settings: formData.settings,
          status: formData.status,
          access: formData.access,
        };
        await aiAgentService.createAIAgent(payload);
      } else if (aiAgent) {
        const payload: AIAgentUpdatePayload = {
          label: formData.label.trim(),
          skills: formData.skills,
          tool_calls: formData.tool_calls,
          system_prompt: formData.system_prompt,
          settings: formData.settings,
          status: formData.status,
          access: showAccessLevel ? formData.access : undefined,
        };
        await aiAgentService.updateAIAgent(
          aiAgent.ai_agent_id,
          aiAgent.tenant_id,
          payload,
          tenantContext.tenant_id
        );
      }
      onSaved();
      onClose();
    } catch (err) {
      const conflict = aiAgentService.extractConflict(err);
      if (conflict) {
        setDuplicate(conflict);
      } else {
        setSaveError(extractSaveErrorMessage(err, 'Failed to save AI Agent. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  // ORCH-363: the conflicting row is soft-deleted — bring it back as-is
  // (same ID, prompt, skills and settings) instead of creating a new one.
  // Restore is a write to a shared row, so the "Restore it" link opens a
  // confirmation first; this runs on confirm.
  const handleRestoreConflict = async () => {
    if (!duplicate) return;
    const { ai_agent_id, tenant_id } = duplicate.conflicting_record;
    setSaveError(null);
    setLoading(true);
    try {
      await aiAgentService.restoreAIAgent(ai_agent_id, tenant_id);
      setRestoreConfirmOpen(false);
      onSaved();
      onClose();
    } catch (err) {
      setRestoreConfirmOpen(false);
      setSaveError(extractSaveErrorMessage(err, 'Failed to restore AI Agent. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Same gate as the grid's restore/delete actions (ORCH-344): a shared GLOBAL
  // row can only be restored from the GLOBAL tenant.
  const canRestoreConflict =
    canEdit &&
    !!duplicate &&
    (duplicate.conflicting_record.tenant_id !== 'GLOBAL' || tenantContext.tenant_id === 'GLOBAL');

  const handleDelete = () => {
    if (!aiAgent || !onDelete) return;
    onDelete(aiAgent);
  };

  const editingGlobalFromTenant =
    mode === 'edit' && aiAgent?.tenant_id === 'GLOBAL' && !tenantContext.is_global;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {tenantContext.is_global ? <GlobalIcon /> : <TenantIcon />}
          {mode === 'create' ? 'Create AI Agent' : 'Edit AI Agent'}
          <Chip
            label={tenantContext.name}
            size="small"
            color={tenantContext.is_global ? 'warning' : 'primary'}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {loadingOptions ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tenantContext.is_global && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are creating or editing a GLOBAL AI Agent. This will be visible to all tenants.
              </Alert>
            )}

            {editingGlobalFromTenant && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You are editing a GLOBAL AI Agent. Saving will create a tenant-specific override.
              </Alert>
            )}

            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}

            {duplicate && (
              <Alert severity="error" sx={{ mb: 2 }} data-testid="ai-agent-duplicate-alert">
                {duplicate.conflicting_record.status === 'deleted' ? (
                  <>
                    {duplicate.error === 'duplicate_name'
                      ? `An agent named '${duplicate.conflicting_record.label}' already exists in scope '${duplicate.conflicting_record.tenant_id}' but is deleted (ID ${duplicate.conflicting_record.ai_agent_id}). `
                      : `An agent with ID ${duplicate.conflicting_record.ai_agent_id} already exists in scope '${duplicate.conflicting_record.tenant_id}' but is deleted. `}
                    {canRestoreConflict ? (
                      <>
                        <Link
                          component="button"
                          type="button"
                          onClick={() => setRestoreConfirmOpen(true)}
                          disabled={loading}
                          data-testid="ai-agent-restore-conflict"
                        >
                          Restore it
                        </Link>
                        {`, or choose a different ${duplicate.error === 'duplicate_name' ? 'name' : 'ID'}.`}
                      </>
                    ) : (
                      `${
                        canEdit && duplicate.conflicting_record.tenant_id === 'GLOBAL'
                          ? 'Only a global admin in the GLOBAL tenant can restore it. '
                          : ''
                      }Choose a different ${duplicate.error === 'duplicate_name' ? 'name' : 'ID'}.`
                    )}
                  </>
                ) : (
                  duplicate.message
                )}
              </Alert>
            )}

            <AIAgentForm
              mode={mode}
              formData={formData}
              errors={errors}
              skillOptions={skillOptions}
              toolCallOptions={toolCallOptions}
              canEdit={canEdit}
              showAccessLevel={showAccessLevel}
              onChange={setFormData}
            />
          </>
        )}
      </DialogContent>

      <DialogFooter
        destructiveAction={
          mode === 'edit' &&
          onDelete &&
          canEdit && (
            <DestructiveButton
              onClick={handleDelete}
              startIcon={<DeleteIcon />}
              disabled={loading}
              data-testid="ai-agent-delete-button"
            >
              Delete
            </DestructiveButton>
          )
        }
      >
        <SecondaryButton onClick={onClose} disabled={loading}>
          Cancel
        </SecondaryButton>
        {canEdit && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || loadingOptions}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
            data-testid="ai-agent-save-button"
          >
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        )}
      </DialogFooter>

      {duplicate && (
        <ConfirmDialog
          open={restoreConfirmOpen}
          title="Restore AI Agent?"
          confirmLabel="Restore"
          loading={loading}
          onConfirm={handleRestoreConflict}
          onClose={() => setRestoreConfirmOpen(false)}
          dataTestId="ai-agent-restore-conflict-dialog"
        >
          <DialogContentText>
            &quot;{duplicate.conflicting_record.label}&quot; (
            {duplicate.conflicting_record.ai_agent_id}) becomes active again with its original
            system prompt, skills and settings. Nothing you typed here is saved.
            {duplicate.conflicting_record.tenant_id === 'GLOBAL' &&
              ' It is a GLOBAL agent, so it becomes visible to every tenant again.'}
          </DialogContentText>
        </ConfirmDialog>
      )}
    </Dialog>
  );
};

export default AIAgentModal;
